import React from 'react';
import { graphql, useFragment } from 'react-relay';
import type { AtlasProjectCell$key } from '@atlassian/jira-relay/src/__generated__/AtlasProjectCell.graphql';
import { AtlasProjectInlineEditViewWithIsEditable } from '@atlassian/jira-issue-field-atlas-project-inline-edit-full/src/ui/atlas-project/index.tsx';
import { useIsInlineEditingExtendedFieldSupportEnabled } from '../../../../controllers/features/selectors.tsx';

import { useLazyLoadEditability } from '../../../../services/lazy-editability-loader/index.tsx';

import { INLINE_EDIT_ANALYTICS_ATTRIBUTES } from '../common/inline-edit-analytics-attributes.tsx';

export type Props = {
	fieldRef: AtlasProjectCell$key;
};

export const AtlasProjectCell = ({ fieldRef }: Props) => {
	const data = useFragment<AtlasProjectCell$key>(
		graphql`
			fragment AtlasProjectCell on JiraTownsquareProjectField
			@argumentDefinitions(isInlineEditingEnabled: { type: "Boolean!" }) {
				id
				lazyIsEditableInIssueView @include(if: $isInlineEditingEnabled)
				...atlasProject_issueFieldAtlasProjectInlineEditFull_AtlasProjectInlineEditViewWithIsEditable_fragmentRef
			}
		`,
		fieldRef,
	);
	const isInlineEditingExtendedFieldSupportEnabled =
		useIsInlineEditingExtendedFieldSupportEnabled();

	// If the property does not exist in fragment data then we know it has been conditionally excluded because inline
	// editing is disabled. In this case we explicitly set isEditable to false to bypass the lazy editability query.
	const isEditable =
		isInlineEditingExtendedFieldSupportEnabled &&
		'lazyIsEditableInIssueView' in data &&
		data.lazyIsEditableInIssueView;

	useLazyLoadEditability(data.id, isEditable);

	return (
		<AtlasProjectInlineEditViewWithIsEditable
			attributes={INLINE_EDIT_ANALYTICS_ATTRIBUTES}
			spacing="default"
			editViewPopup
			editViewPopupAlignBlock="center"
			fragmentRef={data}
			isEditable={!!isEditable}
			menuPosition="fixed"
			readViewFitContainerHeight
			popupPlacement="bottom-start"
		/>
	);
};
